import type { ReactNode } from "react";
import { Badge } from "@/components/ui/badge";
import {
  RowExpanderButton,
  RowExpanderSpacer,
} from "@/features/document-viewport/row-expander-button";
import { cn } from "@/lib/utils";

/**
 * FieldPilot flattens three levels of one hierarchy into a single `<tbody>`: a top-level task
 * (a tag or custom line item), the sub-tasks and item schedules under it, and the milestone
 * drill-down under those. A `<tr>` carries no depth of its own, so every depth cue lives here and
 * is applied identically by the progress table and the schedule dialog (CON-405).
 *
 * The rules, in one place:
 * - **Depth** drives indentation and row tint, so two rows at the same level read as siblings.
 * - The name column is the tree column at every level: indent → elbow → toggle → name.
 *   No other column may hold an expand control, and a task's toggle owns *every* child it has.
 * - Only the ungrouped remainder is badged. A sub-task and an item schedule are told apart by
 *   their name and item count, so badging every row was noise; the remainder is badged because
 *   nothing else on it says it is a derived bucket rather than a record.
 * - A task row opens a new group with a heavy top rule, so groups separate at a glance.
 */
export type FieldPilotRowKind = "task" | "sub-task" | "ungrouped" | "detail";

/** Kinds that render as an aligned row in the host table (everything but the drill-down panel). */
export type FieldPilotTreeRowKind = Exclude<FieldPilotRowKind, "detail">;

const DEPTH_BY_KIND: Record<FieldPilotRowKind, number> = {
  task: 0,
  // Sub-tasks and item schedules share this level and now render identically, so they share one
  // kind; `isSoloItemSchedule` still separates them where the difference matters (ordering and
  // the edit control's label).
  "sub-task": 1,
  // The ungrouped remainder is a peer of the sub-tasks it sits beside — that is exactly how the
  // backend rollup weights it — so it shares their depth.
  ungrouped: 1,
  detail: 2,
};

/** Indent added per level of depth, in rem. */
export const FIELD_PILOT_ROW_INDENT_REM = 1.25;

/**
 * Extra inset applied to the milestone drill-down on top of its depth indent, so its content
 * starts under the sub-task *name* rather than under the sub-task's own elbow.
 */
const DETAIL_CONTENT_INSET_REM = 1.5;

/** Shared cell padding. Keeps every level on the same row height — a child must never be taller. */
export const FIELD_PILOT_ROW_CELL = "px-3 py-1";

/**
 * Vertical rule opening a column band. The progress table reads as three bands — identity,
 * schedule, status — so the appended schedule columns are visibly attached rather than just more
 * columns. Applied to the first cell of each band, in the header and every body row.
 */
export const FIELD_PILOT_COLUMN_BAND = "border-s border-s-border/70";

export function fieldPilotRowDepth(kind: FieldPilotRowKind): number {
  return DEPTH_BY_KIND[kind];
}

/** Left inset for a row's tree content, in rem. */
export function fieldPilotRowIndentRem(kind: FieldPilotRowKind): number {
  const indent = DEPTH_BY_KIND[kind] * FIELD_PILOT_ROW_INDENT_REM;
  return kind === "detail" ? indent + DETAIL_CONTENT_INSET_REM : indent;
}

/**
 * Row chrome for one level. `startsGroup` marks the first row of a new top-level group; `isBehind`
 * paints the schedule-slip accent. Tint comes from depth alone, so a sub-task and an item schedule
 * — siblings — are tinted the same and are told apart by their badge.
 */
export function fieldPilotRowClasses(options: {
  kind: FieldPilotRowKind;
  isBehind?: boolean;
  startsGroup?: boolean;
}): string {
  const { kind, isBehind = false, startsGroup = false } = options;
  const depth = DEPTH_BY_KIND[kind];
  // Depth is tinted with a translucent `foreground` wash rather than `muted`: it darkens the light
  // themes and lightens the dark ones by the same relative step, so the levels stay equally
  // separable in all five themes (`muted` is near-white in Office and would vanish there).
  return cn(
    "h-9",
    depth === 0 && "hover:bg-foreground/[0.04]",
    depth === 1 && "bg-foreground/[0.05] hover:bg-foreground/[0.08]",
    depth === 2 && "bg-foreground/[0.1] hover:bg-foreground/[0.1]",
    startsGroup && "border-t-2 border-t-border",
    isBehind && "border-s-2 border-s-status-error"
  );
}

/**
 * Marks the ungrouped remainder, and nothing else.
 *
 * Dashed, like the "Custom" tag chip, because the row is a derived aggregate rather than a record.
 * Every other row earns its identity from its name, indent and item count.
 */
function FieldPilotRowKindBadge({ kind }: { kind: FieldPilotTreeRowKind }) {
  if (kind !== "ungrouped") {
    return null;
  }
  return (
    <Badge
      variant="outline"
      size="sm"
      className="border-dashed text-muted-foreground"
      title="Items on this task that are not in any sub-task — dates inherited, no milestones"
    >
      Ungrouped
    </Badge>
  );
}

/**
 * Body of the name cell for one row: indent, an elbow connecting it to its parent, the expand
 * toggle, the kind badge, then the name. Pass `onToggle` only when the row actually has children —
 * otherwise a spacer holds the column so names still line up within a level.
 */
export function FieldPilotTreeCellContent({
  kind,
  isExpanded = false,
  onToggle,
  toggleLabel,
  trailing,
  children,
}: {
  kind: FieldPilotTreeRowKind;
  isExpanded?: boolean;
  onToggle?: () => void;
  toggleLabel?: string;
  trailing?: ReactNode;
  children: ReactNode;
}) {
  const depth = DEPTH_BY_KIND[kind];
  return (
    <div
      className="flex min-w-0 items-center gap-1.5"
      style={{ paddingInlineStart: `${depth * FIELD_PILOT_ROW_INDENT_REM}rem` }}
    >
      {depth > 0 ? (
        <span
          aria-hidden="true"
          className="mb-2 -ms-2 h-3 w-2 shrink-0 rounded-bl-[3px] border-b border-s border-border"
        />
      ) : null}
      {onToggle && toggleLabel ? (
        <RowExpanderButton expanded={isExpanded} onToggle={onToggle} label={toggleLabel} />
      ) : (
        <RowExpanderSpacer />
      )}
      <FieldPilotRowKindBadge kind={kind} />
      <div className="min-w-0 flex-1">{children}</div>
      {trailing}
    </div>
  );
}
